import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { api } from './api';
import {
  clearImpersonationState,
  clearStoredAuth,
  getImpersonator,
  getOriginalJwt,
  getStoredJwt,
  setImpersonator as persistImpersonator,
  setOriginalJwt,
  setStoredJwt,
} from './auth-storage';

export type Tier = 'free' | 'basic' | 'pro' | 'unlimited';
export type UserRole = 'user' | 'super_admin';
export type OrgMemberRole = 'admin' | 'member';
export type OrgMemberTier = 'basic' | 'pro' | 'unlimited';

export interface CurrentUser {
  id: number;
  email: string;
  tier: Tier;
  role: UserRole;
}

export interface MeMembership {
  organizationId: number;
  organizationSlug: string;
  organizationName: string;
  role: OrgMemberRole;
  tier: OrgMemberTier;
}

export interface MeResponse {
  user: CurrentUser;
  memberships: MeMembership[];
  // Set when the user is on a corporate domain that doesn't claim an org
  // yet — the portal shows a "Create your team" hint banner.
  orgCreatePromptDomain: string | null;
  usage: {
    date: string;
    count: number;
    inputTokens: number;
    outputTokens: number;
    cap: number | null;
    source: 'override' | 'org_membership' | 'personal';
    remaining: number | null;
  };
  pool: null | {
    organizationId: number;
    budget: number;
    used: number;
    remaining: number;
    includedFromSeats: number;
    purchased: number;
    currentPeriodStart: string;
    currentPeriodEnd: string;
  };
  subscription: null | {
    status: string;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean;
    stripeSubscriptionId: string | null;
  };
}

interface AuthState {
  status: 'loading' | 'signed_out' | 'signed_in';
  me: MeResponse | null;
  impersonator: string | null;
}

interface AuthContextValue extends AuthState {
  signIn: (jwt: string) => Promise<void>;
  signOut: () => void;
  startImpersonation: (jwt: string, impersonatorEmail: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider(props: AuthProviderProps) {
  const { children } = props;
  const [state, setState] = useState<AuthState>({
    status: getStoredJwt() ? 'loading' : 'signed_out',
    me: null,
    impersonator: getImpersonator(),
  });

  const refresh = useCallback(async () => {
    if (!getStoredJwt()) {
      setState({ status: 'signed_out', me: null, impersonator: null });
      return;
    }
    try {
      const me = await api<MeResponse>('/api/me', { allowUnauthorized: true });
      setState({ status: 'signed_in', me, impersonator: getImpersonator() });
    } catch {
      // The api client already cleared the JWT for non-allowed 401s. For
      // other failures we still want to bail out of the loading state so
      // the Login page can show.
      clearStoredAuth();
      setState({ status: 'signed_out', me: null, impersonator: null });
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(
    async (jwt: string) => {
      setStoredJwt(jwt);
      await refresh();
    },
    [refresh],
  );

  const startImpersonation = useCallback(
    async (jwt: string, impersonatorEmail: string) => {
      // Stash the operator's current JWT before swapping in the
      // impersonation token so endImpersonation can restore the session
      // instead of dropping back to the login page.
      const current = getStoredJwt();
      if (current) setOriginalJwt(current);
      setStoredJwt(jwt);
      persistImpersonator(impersonatorEmail);
      await refresh();
    },
    [refresh],
  );

  const endImpersonation = useCallback(async () => {
    const original = getOriginalJwt();
    clearImpersonationState();
    if (original) {
      setStoredJwt(original);
      await refresh();
    } else {
      // No stash (shouldn't happen, but be defensive) — fall back to a
      // proper sign-out so the user lands on the login page rather than
      // continuing as the impersonated user.
      clearStoredAuth();
      setState({ status: 'signed_out', me: null, impersonator: null });
    }
  }, [refresh]);

  const signOut = useCallback(() => {
    clearStoredAuth();
    setState({ status: 'signed_out', me: null, impersonator: null });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signIn, signOut, startImpersonation, endImpersonation, refresh }),
    [state, signIn, signOut, startImpersonation, endImpersonation, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};
